import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Shield, TrendUp, MagnifyingGlass, FileText, Lock, Globe } from '@phosphor-icons/react';

export default function Services() {
  const services = [
    {
      icon: <MagnifyingGlass size={48} weight="duotone" />,
      title: 'Blockchain Analysis',
      description: 'Comprehensive blockchain forensic analysis across all major networks including Bitcoin, Ethereum, and emerging chains.',
      features: [
        'Transaction graph analysis',
        'Multi-chain tracing',
        'Smart contract auditing',
        'Wallet clustering'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/145df3fa-bd42-453d-aed2-434aff38aba8/images/ceb4d7967b79ba77f38ba9b306f9912f3ff79ad502f7b7ffd97735d8307070ab.png'
    },
    {
      icon: <Shield size={48} weight="duotone" />,
      title: 'Crypto Investigations',
      description: 'Professional investigative services to identify fraudulent activities, track stolen funds, and build forensic evidence for legal proceedings.',
      features: [
        'Fraud detection & prevention',
        'Money laundering investigations',
        'Cybercrime tracking',
        'Expert witness services'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/145df3fa-bd42-453d-aed2-434aff38aba8/images/08e93d33567ee95e92da18f1c6a09e414ca836d159a24f1f4f976f3122efce46.png'
    },
    {
      icon: <TrendUp size={48} weight="duotone" />,
      title: 'Asset Tracing',
      description: 'Follow digital assets through complex networks of transactions, mixers, and exchanges to identify final destinations and beneficiaries.',
      features: [
        'Cross-chain tracking',
        'Mixer & tumbler analysis',
        'Exchange deposit tracking',
        'Real-time monitoring'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/145df3fa-bd42-453d-aed2-434aff38aba8/images/76fcbedf72923b55f5ff952c81fc42a814c1df58004d051d1a3b6d908cce48a6.png'
    },
    {
      icon: <FileText size={48} weight="duotone" />,
      title: 'Fraud Detection',
      description: 'Advanced pattern recognition and behavioral analysis to identify fraudulent schemes before they cause significant damage.',
      features: [
        'Scam identification',
        'Ponzi scheme detection',
        'Rug pull prevention',
        'Risk scoring'
      ],
      image: 'https://static.prod-images.emergentagent.com/jobs/145df3fa-bd42-453d-aed2-434aff38aba8/images/08e93d33567ee95e92da18f1c6a09e414ca836d159a24f1f4f976f3122efce46.png'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6" data-testid="services-heading">
              Our Services
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Comprehensive blockchain intelligence and investigation services tailored to your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, idx) => (
              <div key={idx} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`} data-testid={`service-${idx}`}>
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="text-[#0066FF] mb-4">{service.icon}</div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium text-white mb-4">
                    {service.title}
                  </h2>
                  <p className="text-base text-slate-400 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center text-slate-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <Card className="overflow-hidden bg-card border-border">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover"
                    />
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-white mb-12 text-center">
            Additional Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Lock size={32} />, title: 'Security Audits', description: 'Comprehensive security assessments for DeFi protocols and smart contracts.' },
              { icon: <Globe size={32} />, title: 'Compliance Solutions', description: 'AML/KYC compliance tools and reporting for crypto businesses.' },
              { icon: <Shield size={32} />, title: 'Risk Assessment', description: 'Real-time risk scoring for wallets and transactions.' }
            ].map((item, idx) => (
              <Card key={idx} className="p-6 bg-background border-border" data-testid={`additional-service-${idx}`}>
                <div className="text-[#0066FF] mb-4">{item.icon}</div>
                <h3 className="font-heading text-xl font-medium text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
